import axios from 'axios';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import SuccessModal from '../components/modals/SuccessModal';
import SupplierFormModal from '../components/modals/SupplierFormModal';
import { useNotificationModal } from '../hooks/useNotificationModal';
import { formatPhoneWithCountryCode } from '../utils/phoneFormatter';
import DeleteModal from "../components/modals/DeleteModal";
import '../styles/Supplier.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function Supplier() {
  const { userData } = useContext(AuthContext);
  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    showError,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  const [suppliers, setSuppliers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    supplier_name: "",
    email: "",
    phone: "",
    countryCode: "+94",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state: "",
    country: "",
    brn: "",
    vat: "",
    bank_name: "",
    branch: "",
    account_number: "",
    payment_terms: "Cash",
    credit_period: "" || null,
    advance_percentage: "" || null,
    currency_id: "",
  });

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSupplierId, setDeleteSupplierId] = useState(null);

  // Fetch currencies for dropdown
  const fetchCurrencies = useCallback(() => {
    if (!userData?.company_code) return;
    axios
      .get(`${BASE_URL}/api/admin/currencies/get-currencies`, {
        params: { company_code: userData.company_code },
      })
      .then((res) => {
        if (res.data.success && Array.isArray(res.data.currencies)) {
          setCurrencies(res.data.currencies);
        } else {
          setCurrencies([]);
        }
      })
      .catch(() => setCurrencies([]));
  }, [userData?.company_code]);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  const [editingId, setEditingId] = useState(null);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/suppliers/get-suppliers`,
        {
          params: { company_code: userData?.company_code },
        }
      );
      setSuppliers(response.data.suppliers);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  }, [userData?.company_code]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers, userData?.company_code]);

  const handleClose = () => {
    setShowModal(false);
    setFormData({
      supplier_name: "",
      email: "",
      phone: "",
      countryCode: "+94",
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      country: "",
      brn: "",
      vat: "",
      bank_name: "",
      branch: "",
      account_number: "",
      payment_terms: "Cash",
      credit_period: "",
      advance_percentage: "",
      currency_id: "",
    });
    setEditingId(null);
  };

  const handleShow = () => setShowModal(true);

  const handleEdit = (supplier) => {
    setFormData({
      supplier_name: supplier.supplier_name,
      email: supplier.email,
      phone: supplier.phone,
      countryCode: supplier.country_code || "+94",
      address_line_1: supplier.address_line_1 || "",
      address_line_2: supplier.address_line_2 || "",
      city: supplier.city || "",
      state: supplier.state || "",
      country: supplier.country || "",
      brn: supplier.brn,
      vat: supplier.vat || "",
      bank_name: supplier.bank_name || "",
      branch: supplier.branch || "",
      account_number: supplier.account_number || "",
      payment_terms: supplier.payment_terms,
      credit_period: supplier.credit_period || 0,
      advance_percentage: supplier.advance_percentage || null || 0.0,
      currency_id: supplier.currency_id || "",
    });
    setEditingId(supplier.supplier_id);
    setShowModal(true);
  };

  const handleDeleteClick = (supplierId) => {
    setDeleteSupplierId(supplierId);
    setShowDeleteModal(true);
  };

  const handleDeleteSuccess = () => {
    showSuccess("Supplier deleted successfully", "Success");
    fetchSuppliers();
  };

  const handleDeleteError = (error) => {
    showError(error?.message || "Error deleting supplier", "Error");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate conditional fields
    if (formData.payment_terms === "Credit" && !formData.credit_period) {
      showError(
        "Credit period is required for credit payment terms",
        "Validation Error"
      );
      return;
    }

    if (formData.payment_terms === "Advance" && !formData.advance_percentage) {
      showError(
        "Advance percentage is required for advance payment terms",
        "Validation Error"
      );
      return;
    }

    try {
      const cleanPhone = formData.phone.replace(/\D/g, "");

      const supplierData = {
        ...formData,
        country_code: formData.countryCode,
        phone: cleanPhone,
      };
      // Remove countryCode from the data being sent
      delete supplierData.countryCode;

      if (editingId) {
        await axios.put(
          `${BASE_URL}/api/admin/suppliers/update-suppliers/${editingId}`,
          {
            ...supplierData,
            company_code: userData?.company_code,
          }
        );
        showSuccess("Supplier updated successfully!", "Success");
      } else {
        const response = await axios.post(
          `${BASE_URL}/api/admin/suppliers/add-suppliers`,
          {
            ...supplierData,
            company_code: userData?.company_code,
            created_by: userData?.id,
          }
        );

        // Show success message with generated supplier ID
        if (response.data.success && response.data.supplier_id) {
          showSuccess(
            `Supplier created successfully!`,
            "Success"
          );
        }
      }
      handleClose();
      fetchSuppliers();
    } catch (error) {
      console.error("Error saving supplier:", error);
      showError(
        error.response?.data?.message ||
          "Error saving supplier. Please try again.",
        "Error"
      );
    }
  };

  const filteredSuppliers = suppliers.filter(
    (supplier) =>
      supplier.supplier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.supplier_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.vat?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.bank_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="supplier-container">
      <div className="supplier-header">
        <div className="supplier-title">
          <h1> Suppliers </h1>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search suppliers..."
            className="form-control"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button variant="primary" onClick={handleShow}>
          Add Supplier
        </Button>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Phone</th>
            <th>Email</th>
            <th>Address</th>
            <th>BRN</th>
            <th>VAT</th>
            <th>Bank Details</th>
            <th>Currency</th>
            <th>Payment Terms</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredSuppliers.map((supplier, index) => {
            // Find currency object for this supplier
            const currency = currencies.find(
              (cur) => String(cur.currency_id) === String(supplier.currency_id)
            );
            return (
              <tr key={supplier.supplier_id}>
                <td>{index + 1}</td>
                <td>{supplier.supplier_name}</td>
                <td>
                  {formatPhoneWithCountryCode(
                    supplier.country_code,
                    supplier.phone
                  )}
                </td>
                <td>{supplier.email}</td>
                <td>
                  {[
                    supplier.address_line_1,
                    supplier.address_line_2,
                    supplier.city,
                    supplier.state,
                    supplier.country,
                  ]
                    .filter((part) => part && part.trim())
                    .join(", ")}
                </td>
                <td>{supplier.brn}</td>
                <td>{supplier.vat}</td>
                <td>
                  {supplier.bank_name && (
                    <div>
                      <div>
                        <strong>{supplier.bank_name}</strong>
                      </div>
                      <div>{supplier.branch}</div>
                      <div>{supplier.account_number}</div>
                    </div>
                  )}
                </td>
                <td>
                  {currency
                    ? `${currency.currency_name} (${currency.short_name})`
                    : ""}
                </td>
                <td>
                  <div>{supplier.payment_terms}</div>
                  {supplier.payment_terms === "Credit" &&
                    supplier.credit_period && (
                      <small>({supplier.credit_period} days)</small>
                    )}
                  {supplier.payment_terms === "Advance" &&
                    supplier.advance_percentage && (
                      <small>({supplier.advance_percentage}%)</small>
                    )}
                </td>
                <td>
                  <FaEdit
                    className="action-icon edit"
                    onClick={() => handleEdit(supplier)}
                    style={{ color: "#007bff !important" }}
                  />
                  <FaTrash
                    className="action-icon delete"
                    onClick={() => handleDeleteClick(supplier.supplier_id)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <SupplierFormModal
        show={showModal}
        onHide={handleClose}
        onSubmit={handleSubmit}
        editingId={editingId}
        formData={formData}
        setFormData={setFormData}
        currencies={currencies}
        onCurrencyAdded={fetchCurrencies}
      />

      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        id={deleteSupplierId}
        deleteUrl={`/api/admin/suppliers/delete-suppliers/:id`}
        entityLabel="supplier"
        modalTitle="Delete Supplier"
        onDeleteSuccess={handleDeleteSuccess}
        onDeleteError={handleDeleteError}
      />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        variant="success"
        modalTitle={successTitle}
        message={successMessage}
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        variant="error"
        modalTitle={errorTitle}
        message={errorMessage}
      />
    </div>
  );
}
